/**
 * SignaturePad — DRAW (canvas), UPLOAD (PNG/JPEG ≤ 1MB), or TYPE (full name in
 * one of 10 handwriting fonts, rasterized to a PNG). Emits the PNG data URL via
 * onChange (null while empty). Used on the signing page (white card context).
 */
import { useEffect, useRef, useState } from 'react';
import { SIGNATURE_FONTS, SIGNATURE_INK, SIGNATURE_PAPER, paperStyle, renderTypedSignature, type SignatureFont } from './signatureFonts';
import { btnGhost, segGroup, segItemOn, segItemOff } from '../design/ui';

type Mode = 'draw' | 'upload' | 'type' | 'saved';

export function SignaturePad({
  onChange,
  saved,
  typeName,
  onFontChange,
  onMethodChange,
}: {
  onChange: (dataUrl: string | null) => void;
  /** The user's reusable profile signature (data URL) — adds a "Use saved" option. */
  saved?: string | null;
  /** The signer's full name — enables the "Type" option (name in a script font). */
  typeName?: string;
  /** Emits the chosen script font when the signer TYPES (null otherwise) — lets
   *  a parent render matching initials in the same font. */
  onFontChange?: (font: SignatureFont | null) => void;
  /** Emits how the signature was produced (for a "save to profile" offer). */
  onMethodChange?: (method: Mode) => void;
}) {
  const [mode, setMode] = useState<Mode>(saved ? 'saved' : 'draw');
  const [typedFont, setTypedFont] = useState<SignatureFont | null>(null);
  // Preselect the saved signature when it exists.
  useEffect(() => {
    if (saved && mode === 'saved') onChange(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    // Crisp lines on retina.
    const scale = window.devicePixelRatio || 1;
    c.width = 520 * scale;
    c.height = 160 * scale;
    ctx.scale(scale, scale);
    ctx.fillStyle = SIGNATURE_PAPER;
    ctx.fillRect(0, 0, 520, 160);
    ctx.strokeStyle = SIGNATURE_INK;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [mode]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 520,
      y: ((e.clientY - rect.top) / rect.height) * 160,
    };
  };
  const emit = () => {
    const c = canvasRef.current;
    if (c) onChange(c.toDataURL('image/png'));
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.fillStyle = SIGNATURE_PAPER;
    ctx.fillRect(0, 0, 520, 160);
    setHasInk(false);
    onChange(null);
  };

  const onFile = (f: File | undefined) => {
    setUploadErr(null);
    if (!f) return;
    if (!['image/png', 'image/jpeg'].includes(f.type)) {
      setUploadErr('PNG or JPEG only.');
      return;
    }
    if (f.size > 1024 * 1024) {
      setUploadErr('Max 1 MB.');
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const url = String(r.result);
      setUploaded(url);
      onChange(url);
    };
    r.readAsDataURL(f);
  };

  const tab = (active: boolean) => (active ? segItemOn : segItemOff);

  const pickFont = async (font: SignatureFont) => {
    if (!typeName) return;
    setTypedFont(font);
    onFontChange?.(font);
    onChange(await renderTypedSignature(typeName, font));
  };
  const go = (m: Mode) => { setMode(m); onMethodChange?.(m); };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className={segGroup}>
        {saved && (
          <button type="button" className={tab(mode === 'saved')} onClick={() => { go('saved'); setTypedFont(null); onFontChange?.(null); onChange(saved); }}>
            Use saved
          </button>
        )}
        <button type="button" className={tab(mode === 'draw')} onClick={() => { go('draw'); setUploaded(null); setTypedFont(null); onFontChange?.(null); onChange(null); }}>
          Draw
        </button>
        {typeName && (
          <button
            type="button"
            className={tab(mode === 'type')}
            onClick={() => { go('type'); setUploaded(null); setHasInk(false); if (typedFont) void pickFont(typedFont); else onChange(null); }}
          >
            Type
          </button>
        )}
        <button type="button" className={tab(mode === 'upload')} onClick={() => { go('upload'); setHasInk(false); setTypedFont(null); onFontChange?.(null); onChange(uploaded); }}>
          Upload image
        </button>
        </div>
        {mode === 'draw' && hasInk && (
          <button type="button" className={`${btnGhost} ml-auto`} onClick={clear}>
            Clear
          </button>
        )}
      </div>
      {mode === 'saved' && saved ? (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-4 flex flex-wrap items-center gap-4">
          <img src={saved} alt="Your saved signature" className="max-h-24 max-w-full sm:max-w-[280px] object-contain rounded p-1" style={paperStyle} />
          <span className="text-xs text-fg-faint">Your saved signature (manage it on your profile).</span>
        </div>
      ) : mode === 'type' && typeName ? (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-3">
          <p className="text-xs text-fg-faint mb-2">Pick a style for your name — it becomes your signature.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
            {SIGNATURE_FONTS.map((f) => (
              <button
                key={f.family}
                type="button"
                onClick={() => void pickFont(f)}
                className={`text-left px-3 py-2 rounded-md border transition-colors ${
                  typedFont?.family === f.family ? 'border-primary ring-2 ring-primary' : 'border-line hover:border-line-strong'
                }`}
                style={{ ...paperStyle, fontFamily: `"${f.family}"`, fontWeight: f.weight }}
              >
                <span className="text-2xl leading-none">{typeName}</span>
              </button>
            ))}
          </div>
        </div>
      ) : mode === 'draw' ? (
        <canvas
          ref={canvasRef}
          className="w-full max-w-[520px] h-[160px] rounded-md border border-dashed border-line-strong touch-none cursor-crosshair"
          style={paperStyle}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      ) : (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-4">
          {uploaded ? (
            <div className="flex items-center gap-4">
              <img src={uploaded} alt="Signature" className="max-h-24 max-w-[280px] object-contain rounded p-1" style={paperStyle} />
              <button type="button" className="text-xs text-link underline" onClick={() => { setUploaded(null); onChange(null); }}>
                Remove
              </button>
            </div>
          ) : (
            <label className="block text-sm text-fg-muted cursor-pointer">
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <span className="underline text-link">Choose an image</span> of your signature (PNG/JPEG, max 1 MB).
            </label>
          )}
          {uploadErr && <p className="text-xs text-danger mt-2">{uploadErr}</p>}
        </div>
      )}
    </div>
  );
}
